import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Tabs, Modal } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateOpenModalControlPanelBank,
  updateLiftSelected,
  updateBankSelected,
  updateChannelId,
  updateServerId,
} from "../../../redux/monitoring/monitoringSlice";
import { useLazyGetServerQuery } from "../../../redux/servers/serverAPI";
// CUSTOM HOOKS
import { useLastBankMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import ButtonClose from "../../components/buttons/ButtonClose";
import ControlPanelBankHeader from "./ControlPanelBankHeader";
import ControlPanelBankActions from "./ControlPanelBankActions";
import ControlPanelBankFaults from "./ControlPanelBankFaults";
import ControlPanelBankScheduledActions from "./ControlPanelBankScheduledActions";
// INTERFACES
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
import { DeviceServiceContent } from "../../../flatbuffers/generated/service/device-service-content";
import { Service } from "../../../flatbuffers/generated/service/service";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { BLANK_LIFT } from "../../../constants/lifts";
import { BLANK_BANK } from "../../../constants/bank";
import { BLANK_SERVER } from "../../../constants/server";
// UTILS
import { stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";
import formatUuidT from "../../../flatbuffers/display/uuid";
// SCOPE
import {
  useCanOpenControlPanel,
  useEffectivePermission,
} from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

function ControlPanelBank() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const dispatch = useDispatch();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [tabActiveKey, setTabActiveKey] = useState<string>("ACTIONS");
  const [liftVip, setLiftVip] = useState<any>({});
  const [socketMessageVip, setSocketMessageVip] = useState<SocketMessageInterface>({
    ...BLANK_SOCKET_MESSAGE,
  });
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [server, setServer] = useState<ServerInterface>({ ...BLANK_SERVER });

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { openModalControlPanelBank, controlPanelDrawerPlacement, bankSelected, serverId } =
    useSelector((state: RootState) => state.monitoring);
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { userLogged } = useSelector((state: RootState) => state.home);
  // Per-floor ACTION scope predicate — empty rows ⇒ allow-all, master admin ⇒ all.
  // Gates the bank-level actions panels (Hall Calls / VIP) which both render
  // floor buttons from the bankFloors prop computed below.
  // Gate the Actions tab itself: same feature permission the lift drawer's
  // Actions tab uses, but scoped to the BANK only — no liftId, since this
  // tab shows bank-level commands, never a single car's. Empty scope grant
  // rows ⇒ allow-all; master admin ⇒ always allowed.
  // No tab would render ⇒ the drawer must not open, whatever set the redux flag.
  const canOpenPanel = useCanOpenControlPanel("bank");
  const bankActionsAllowed = useEffectivePermission(
    Permission.MonitorControlPanelActions,
    PermissionAction.ACTION,
    { bankId: formatUuidT(bankSelected.id) },
  );

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetServer] = useLazyGetServerQuery();

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const bankUuid = useMemo(
    () => (bankSelected.id ? stringToUuidT(String(bankSelected.id)) : EMPTY_UUID),
    [bankSelected.id],
  );
  const socketMessageBank: SocketMessageInterface =
    useLastBankMessage(bankUuid) || BLANK_SOCKET_MESSAGE;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const isVipMode = (msg: SocketMessageInterface): boolean =>
    msg.serviceMode?.messageType === DeviceServiceContent.ElevatorServiceMessage &&
    msg.serviceMode?.message?.message?.message === Service.VIP;

  const getServer = async () => {
    const token = await GETJwtToken();
    try {
      const result = await triggerGetServer({
        token,
        idServer: serverId,
      }).unwrap();
      if (result && result.data) {
        setServer(result.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const clearVipMode = () => {
    setLiftVip({});
    setSocketMessageVip({
      ...BLANK_SOCKET_MESSAGE,
    });
  };

  const onClickClose = () => {
    if (liftVip.id && socketMessageVip.bankId) {
      setOpenModal(true);
    } else {
      dispatch(updateOpenModalControlPanelBank(false));
      dispatch(updateLiftSelected({ ...BLANK_LIFT }));
      dispatch(updateBankSelected({ ...BLANK_BANK }));
      dispatch(updateChannelId(""));
      dispatch(updateServerId(""));
    }
  };

  const onClickCancel = () => {
    setOpenModal(false);
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    // The socket message carries `liftId` as a FlatBuffer `UuidT` object, while
    // `bankSelected.lifts[].id` arrives from the REST bank payload as a plain
    // string. Comparing the two with `===` is always false and fails silently,
    // which left the VIP car never latched and the header's "Car VIP:" tag
    // permanently unrendered. Normalize both sides to the canonical dashed
    // string first — `formatUuidT` accepts either representation.
    const messageLiftId = formatUuidT(socketMessageBank.liftId);
    if (isVipMode(socketMessageBank)) {
      setSocketMessageVip(socketMessageBank);
      // `formatUuidT` renders an absent id as "", so an empty normalization on
      // the message side must never match an equally id-less lift row.
      const index = (bankSelected.lifts ?? []).findIndex(
        (object: any) => messageLiftId !== "" && formatUuidT(object.id) === messageLiftId,
      );
      if (index >= 0) {
        setLiftVip((bankSelected.lifts ?? [])[index]);
      }
    }
    if (
      liftVip &&
      liftVip.id &&
      messageLiftId !== "" &&
      formatUuidT(liftVip.id) === messageLiftId &&
      !isVipMode(socketMessageBank)
    ) {
      setTimeout(() => {
        // Forget the latched car once the pulse window has passed, so the
        // header stops advertising a car that already left VIP service.
        // This used to share the window with a `vipServiceModeStopped` flag
        // read by the VIP tab; the tab is gone — VIP runs as a sequenced
        // action now — and the flag went with it, but the clearing did not:
        // nothing else clears `liftVip`.
        clearVipMode();
      }, 100);
    }
  }, [socketMessageBank]);

  useEffect(() => {
    // Actions is the default tab for every bank type — it carries the
    // hall-call and VIP commands that used to have their own panes.
    setTabActiveKey("ACTIONS");
  }, [bankSelected]);

  useEffect(() => {
    if (openModalControlPanelBank) {
      setLiftVip({});
    }
  }, [openModalControlPanelBank]);

  useEffect(() => {
    if (serverId !== "") {
      getServer();
    }
  }, [serverId]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Drawer
      width={650}
      placement={controlPanelDrawerPlacement}
      open={openModalControlPanelBank && canOpenPanel}
      closable={false}
      onClose={onClickClose}
      footer={
        <div style={{ textAlign: "right" }}>
          <ButtonClose onClick={onClickClose} />
        </div>
      }
    >
      <div>
        <ControlPanelBankHeader liftVip={liftVip} server={server} />
      </div>
      <br />
      <div>
        <Tabs
          onTabClick={(key: string) => {
            setTabActiveKey(key);
          }}
          activeKey={tabActiveKey}
          items={[
            bankActionsAllowed
              ? {
                  key: "ACTIONS",
                  label: "Actions",
                  children: (
                    <ControlPanelBankActions
                      bankId={String(bankSelected.id ?? "")}
                      bankName={String(bankSelected.name ?? "")}
                    />
                  ),
                }
              : null,
            hasPermission(Permission.MonitorControlPanelFaults, permissionSet ?? [])
              ? {
                  key: "FAULTS",
                  label: "Faults",
                  children: <ControlPanelBankFaults />,
                }
              : null,
            hasPermission(Permission.ScheduledActionsView, permissionSet ?? [])
              ? {
                  key: "SCHEDULED_ACTIONS",
                  label: t("scheduledActions.scheduledActionsTab", "Scheduled Actions"),
                  children: (
                    <ControlPanelBankScheduledActions
                      bankId={String(bankSelected.id ?? "")}
                      organizationId={userLogged?.organizationId ?? ""}
                      bankName={
                        typeof bankSelected.name === "string" ? bankSelected.name : undefined
                      }
                    />
                  ),
                }
              : null,
          ].filter((item): item is NonNullable<typeof item> => item != null)}
        />
      </div>
      <Modal
        title={t("general.warningCloseVipModal")}
        open={openModal}
        onOk={onClickClose}
        onCancel={onClickCancel}
      >
        <p>{t("general.warningCloseVipModalDescription")}</p>
      </Modal>
    </Drawer>
  );
}

export default ControlPanelBank;
